import { useRef, useEffect, useState } from 'react';

interface UseStickyPositionOptions {
  /**
   * Offset from the top when sticky (in pixels)
   * @default 24
   */
  topOffset?: number;
  /**
   * Delay before initializing scroll listeners (in milliseconds)
   * @default 100
   */
  initDelay?: number;
}

interface UseStickyPositionReturn {
  containerRef: React.RefObject<HTMLDivElement>;
  isSticky: boolean;
  stickyTop: number;
}

/**
 * Finds all scrollable parent containers of an element
 */
const findScrollContainers = (
  element: HTMLElement | null
): (HTMLElement | Window)[] => {
  const containers: (HTMLElement | Window)[] = [window];

  if (!element) return containers;

  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY || style.overflow;
    const height = parent.clientHeight;
    const scrollHeight = parent.scrollHeight;

    // Check if this element is scrollable
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      scrollHeight > height
    ) {
      containers.push(parent);
    }
    parent = parent.parentElement;
  }
  return containers;
};

/**
 * Custom hook for managing sticky positioning of an element.
 * The element becomes sticky when its bottom edge enters the viewport.
 *
 * @param options - Configuration options for sticky positioning
 * @returns Object containing ref, sticky state, and top position
 */
export const useStickyPosition = (
  options: UseStickyPositionOptions = {}
): UseStickyPositionReturn => {
  const { topOffset = 70, initDelay = 100 } = options;
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyTop, setStickyTop] = useState(0);
  const isStickyRef = useRef(false);
  const listenersRef = useRef<
    Array<{
      container: HTMLElement | Window;
      handler: () => void;
      type: 'scroll' | 'resize';
    }>
  >([]);

  useEffect(() => {
    const updateStickyState = () => {
      if (!containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      const elementHeight = rect.height;
      const defaultStickyTop = topOffset;
      
      // Check if element should be sticky (when it would scroll past the topOffset)
      if (rect.top <= topOffset) {
        // Element should be sticky
        const bottomIfStuckAtDefault = defaultStickyTop + elementHeight;
        
        // Check if bottom of the element would go outside screen when stuck at default position
        if (bottomIfStuckAtDefault > viewportHeight) {
          // Adjust sticky position to keep bottom within viewport
          const calculatedTop = viewportHeight - elementHeight;
          if (!isStickyRef.current || stickyTop !== calculatedTop) {
            setStickyTop(calculatedTop);
            setIsSticky(true);
            isStickyRef.current = true;
          }
        } else {
          // Use default 70px from top
          if (!isStickyRef.current || stickyTop !== defaultStickyTop) {
            setStickyTop(defaultStickyTop);
            setIsSticky(true);
            isStickyRef.current = true;
          }
        }
      } else {
        // Element should not be sticky
        if (isStickyRef.current) {
          setIsSticky(false);
          isStickyRef.current = false;
        }
      }
    };

    const timeoutId = setTimeout(() => {
      if (!containerRef.current) return;

      // Find all scrollable containers
      const scrollContainers = findScrollContainers(containerRef.current);

      // Add listeners to all scrollable containers
      scrollContainers.forEach((container) => {
        if (container === window) {
          window.addEventListener('scroll', updateStickyState, {
            passive: true
          });
          window.addEventListener('resize', updateStickyState, {
            passive: true
          });
          listenersRef.current.push(
            { container: window, handler: updateStickyState, type: 'scroll' },
            { container: window, handler: updateStickyState, type: 'resize' }
          );
        } else {
          const element = container as HTMLElement;
          element.addEventListener('scroll', updateStickyState, {
            passive: true
          });
          listenersRef.current.push({
            container: element,
            handler: updateStickyState,
            type: 'scroll'
          });
        }
      });

      // Also add to documentElement and body for safety
      document.documentElement.addEventListener('scroll', updateStickyState, {
        passive: true,
        capture: true
      });
      document.body.addEventListener('scroll', updateStickyState, {
        passive: true,
        capture: true
      });
      listenersRef.current.push(
        {
          container: document.documentElement,
          handler: updateStickyState,
          type: 'scroll'
        },
        { container: document.body, handler: updateStickyState, type: 'scroll' }
      );

      // Initial check
      updateStickyState();
    }, initDelay);

    return () => {
      clearTimeout(timeoutId);

      // Remove all listeners
      listenersRef.current.forEach(({ container, handler, type }) => {
        try {
          if (container === window) {
            window.removeEventListener(type, handler);
          } else {
            const element = container as HTMLElement;
            // Check if it's document, documentElement, or body (which need capture: true)
            if (
              element === document.documentElement ||
              element === document.body
            ) {
              element.removeEventListener(type, handler, true);
            } else {
              element.removeEventListener(type, handler);
            }
          }
        } catch (e) {
        }
      });
      listenersRef.current = [];
    };
  }, [topOffset, initDelay]);

  return {
    containerRef,
    isSticky,
    stickyTop
  };
};
